import { Typography } from '@material-ui/core';
import { Box, Grid, Modal } from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { globalColors } from '../../../../../../../_metronic/_assets/sass/custom/globalColors';
import { AppAPIService } from '../../../../../../services/AppAPIService';
import { Button } from './styles';

export const ConfirmModal = ({ isOpen, handleOpen, crawlerParams, onStarted }) => {
  let api = AppAPIService.getInstance();

  const [loading, setLoading] = useState(false);

  const formattedParams = useMemo(() => ({
    days: crawlerParams?.periodInDays || '-',
    value: formatCurrency(crawlerParams?.minimumValue),
    type: crawlerParams?.type || '-',
    state: crawlerParams?.state === 'TODOS' ? 'Todos os estados' : crawlerParams?.state || '-',
    city: crawlerParams?.city === 'TODAS' ? 'Todas as cidades' : crawlerParams?.city || '-',
    documentType: crawlerParams?.documentType === 'TODOS' ? 'Todos' : crawlerParams?.documentType || '-',
  }), [crawlerParams]);

  const handleSubmit = useCallback(async () => {
    const nextCycleId = `portal-crawler-${Date.now()}`;
    setLoading(true);
    try {
      const response = await api.makeHttpRequest({
        url: '/projudi/params',
        method: 'GET',
        params: {
          ...crawlerParams,
          cycleId: nextCycleId,
        },
      });
      if (onStarted) {
        onStarted({
          cycleId: response?.cycleId || nextCycleId,
          params: crawlerParams,
          initialStatus: response,
        });
      }
      handleOpen(false);
    } catch (e) {
      const status = Number(e?.response?.status || 0);
      const isServerOrTimeoutError = !status || status >= 500;

      if (isServerOrTimeoutError) {
        if (onStarted) {
          onStarted({
            cycleId: nextCycleId,
            params: crawlerParams,
            warning: 'A execução foi solicitada, mas a API demorou ou retornou instabilidade ao confirmar o início. O acompanhamento continuará pelo identificador do ciclo.',
          });
        }
        handleOpen(false);
        return;
      }

      if (e?.response) {
        Swal.fire({
          icon: 'error',
          title: 'Erro ao iniciar execução',
          text: e?.response?.data?.message || 'Não foi possível iniciar o crawler com os parâmetros informados.',
        });
        return;
      }
      if (onStarted) {
        onStarted({
          cycleId: nextCycleId,
          params: crawlerParams,
          warning: 'A execução foi solicitada, mas a resposta inicial demorou mais que o esperado. O acompanhamento continuará pelo identificador do ciclo.',
        });
      }
      handleOpen(false);
    } finally {
      setLoading(false);
    }
  }, [api, crawlerParams, handleOpen, onStarted]);

  const handleClose = useCallback(() => {
    if (loading) return;
    handleOpen(false);
  }, [handleOpen, loading]);

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        disableEnforceFocus
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 999,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              padding: '28px',
              borderRadius: '6px',
              boxShadow: 24,
              width: 'min(760px, calc(100vw - 32px))',
              maxHeight: 'calc(100vh - 56px)',
              overflowY: 'auto',
            }}
          >
            <Typography
              variant="h5"
              id="modal-modal-title"
              component="h2"
              style={{
                marginBottom: '8px',
                marginTop: 0,
                color: '#3f4254',
                fontWeight: 600,
              }}
            >
              Forçar execução do crawler
            </Typography>
            <Typography
              component="p"
              style={{
                color: '#7e8299',
                fontSize: '13px',
                marginBottom: '20px',
              }}
            >
              Confirme os parâmetros abaixo antes de iniciar a busca. O acompanhamento ficará disponível na própria tela, sem bloquear o uso do portal.
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: '10px',
                marginBottom: '22px',
                '@media (max-width: 720px)': {
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                },
              }}
            >
              <InfoTile label="Período" value={`${formattedParams.days} dia(s)`} />
              <InfoTile label="Valor mínimo" value={formattedParams.value} />
              <InfoTile label="Tipo" value={formattedParams.type} />
              <InfoTile label="Estado" value={formattedParams.state} />
              <InfoTile label="Cidade" value={formattedParams.city} />
              <InfoTile label="CPF/CNPJ" value={formattedParams.documentType} />
            </Box>
            <Grid
              container
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                marginTop: '20px',
                gap: '10px',
              }}
            >
              <Button
                color={globalColors.primaryColor2}
                onClick={handleClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                type="submit"
                color={globalColors.primaryColor2}
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Iniciando...' : 'Confirmar execução'}
              </Button>
            </Grid>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

const InfoTile = ({ label, value }) => (
  <Box
    sx={{
      border: '1px solid #ebedf3',
      borderRadius: '6px',
      padding: '10px 12px',
      minWidth: 0,
    }}
  >
    <Typography style={{ color: '#7e8299', fontSize: '11px', textTransform: 'uppercase' }}>
      {label}
    </Typography>
    <Typography
      style={{
        color: '#3f4254',
        fontSize: '13px',
        fontWeight: 600,
        overflowWrap: 'anywhere',
      }}
    >
      {value}
    </Typography>
  </Box>
);

const formatCurrency = (rawValue) => {
  const value = Number(rawValue || 0);
  if (!Number.isFinite(value) || value <= 0) return '-';
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};
